import { appreciatedValue } from '../calculations/appreciation.ts';
import { monthsBetween, type Day } from '../core/calendar.ts';
import { dec, div, mul, negate, sub, sum, type Decimal } from '../core/decimal.ts';
import type {
  Asset, AssetClass, DepreciatingAsset, DepreciationPreset, DepreciationRates, Property, ValueTrajectory,
} from '../types/domain.ts';
import { PRESET_RATES, rateForYear } from './depreciation.ts';

/** Unified-asset rates: custom rates override any preset (divergence D3). */
const trajectoryRates = (preset: keyof typeof PRESET_RATES, custom: DepreciationRates | null): DepreciationRates =>
  custom ?? PRESET_RATES[preset];

/**
 * Asset.value(at:). Appreciating: Appreciation.value (R5). Depreciating: full years at that year's rate, then each remaining month at
 * (yearRate/12)% — all Decimal. Stable: price. Sale events are ignored.
 */
export function assetValue(asset: Asset, date: Day): Decimal {
  const trajectory = asset.valueTrajectory;
  switch (trajectory.kind) {
    case 'appreciating':
      return appreciatedValue(asset.purchasePrice, trajectory.annualRate, asset.purchaseDate, date);
    case 'depreciating': {
      const months = Math.max(0, monthsBetween(asset.purchaseDate, date));
      return depreciatedValue(asset.purchasePrice, months, trajectoryRates(trajectory.preset, trajectory.custom));
    }
    case 'stable':
      return asset.purchasePrice;
  }
}

/** Month-stepped depreciation (divergence D2a). */
export function depreciatedValue(price: Decimal, months: number, rates: DepreciationRates): Decimal {
  const years = Math.trunc(months / 12);
  const remainingMonths = months % 12;
  let value = price;
  for (let year = 0; year < years; year++) {
    value = mul(value, sub(dec(1), div(rateForYear(rates, year), dec(100))));
  }
  const monthlyRate = div(rateForYear(rates, years), dec(12));
  for (let month = 0; month < remainingMonths; month++) {
    value = mul(value, sub(dec(1), div(monthlyRate, dec(100))));
  }
  return value;
}

/** Asset.saleProceeds(at:): s − s × sellingCosts/100 with s = expectedPrice ?? value(date). */
export function assetSaleProceeds(asset: Asset, date: Day): Decimal {
  const saleValue = asset.saleEvent?.expectedPrice ?? assetValue(asset, date);
  const costs = div(mul(saleValue, asset.saleEvent?.sellingCostsPercent ?? dec(0)), dec(100));
  return sub(saleValue, costs);
}

/** ValueTrajectory.effectiveAnnualRate: rate; −(y1 + y2 + y3)/3 for depreciation; 0 when stable. */
export function effectiveAnnualRate(trajectory: ValueTrajectory): Decimal {
  switch (trajectory.kind) {
    case 'appreciating':
      return trajectory.annualRate;
    case 'depreciating': {
      const r = trajectoryRates(trajectory.preset, trajectory.custom);
      return negate(div(sum([r.year1, r.year2, r.year3Plus]), dec(3)));
    }
    case 'stable':
      return dec(0);
  }
}

/** AssetClass.defaultsToAppreciation: only property appreciates by default. */
export const defaultsToAppreciation = (assetClass: AssetClass): boolean => assetClass === 'property';

/** Asset.fromProperty: an appreciating property asset carrying the sale price as expected price. */
export const assetFromProperty = (property: Property): Asset => ({
  id: property.id,
  label: property.label,
  isActive: property.isActive,
  purchasePrice: property.propertyValue,
  purchaseDate: property.startDate,
  assetClass: 'property',
  valueTrajectory: { kind: 'appreciating', annualRate: property.annualAppreciationRate },
  saleEvent:
    property.saleEvent === null
      ? null
      : {
          date: property.saleEvent.date,
          expectedPrice: property.saleEvent.salePrice,
          sellingCostsPercent: property.saleEvent.sellingCostsPercent,
        },
});

const LEGACY_ASSET_CLASS: Readonly<Record<DepreciationPreset, AssetClass>> = {
  'vehicle-standard': 'vehicle',
  'vehicle-truck': 'vehicle',
  'vehicle-luxury': 'vehicle',
  'vehicle-electric': 'vehicle',
  electronics: 'electronics',
  instruments: 'furniture',
  slow: 'other',
  custom: 'other',
};

/** Asset.fromDepreciatingAsset: class from the preset; the sale keeps its price but drops selling costs. */
export const assetFromDepreciatingAsset = (asset: DepreciatingAsset): Asset => ({
  id: asset.id,
  label: asset.label,
  isActive: asset.isActive,
  purchasePrice: asset.purchasePrice,
  purchaseDate: asset.purchaseDate,
  assetClass: LEGACY_ASSET_CLASS[asset.depreciationPreset],
  valueTrajectory: { kind: 'depreciating', preset: asset.depreciationPreset, custom: asset.customDepreciation },
  saleEvent:
    asset.saleEvent === null
      ? null
      : { date: asset.saleEvent.date, expectedPrice: asset.saleEvent.expectedPrice, sellingCostsPercent: dec(0) },
});
